import { useCallback, useMemo } from 'react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const SubGroupOptions = () => {
  const { colDefs, setColDefs, gridApi } = useTableContext();

  const currentPrimaryGroup = useMemo(() => {
    return colDefs?.find((c) => c.rowGroup && c.rowGroupIndex === 0) || null;
  }, [colDefs]);

  const currentSubGroup = useMemo(() => {
    return colDefs?.find((c) => c.rowGroup && c.rowGroupIndex === 1) || null;
  }, [colDefs]);

  const clearSubGrouping = useCallback(() => {
    const liveDefs = (gridApi?.getColumnDefs && gridApi.getColumnDefs()) || colDefs || [];
    const grouped = liveDefs
      .filter(
        (c) => c.rowGroup === true || (typeof c.rowGroupIndex === 'number' && c.rowGroupIndex >= 0)
      )
      .map((c) => ({
        colId: c.colId || c.field,
        index: typeof c.rowGroupIndex === 'number' ? c.rowGroupIndex : 0
      }))
      .sort((a, b) => a.index - b.index);

    const primary = grouped[0];

    if (gridApi?.applyColumnState) {
      const state = primary ? [{ colId: primary.colId, rowGroup: true, rowGroupIndex: 0 }] : [];
      gridApi.applyColumnState({
        defaultState: { rowGroup: false, rowGroupIndex: null },
        state
      });
    }

    setColDefs((prev) =>
      prev.map((c) => {
        const colId = c.colId || c.field;
        if (primary && colId === primary.colId) return { ...c, rowGroup: true, rowGroupIndex: 0 };
        return { ...c, rowGroup: false, rowGroupIndex: null };
      })
    );
  }, [gridApi, setColDefs, colDefs]);

  const subGroupByField = useCallback(
    (field) => {
      if (!field) return clearSubGrouping();
      const liveDefs = (gridApi?.getColumnDefs && gridApi.getColumnDefs()) || colDefs || [];

      const grouped = liveDefs
        .filter(
          (c) =>
            c.rowGroup === true || (typeof c.rowGroupIndex === 'number' && c.rowGroupIndex >= 0)
        )
        .map((c) => ({
          colId: c.colId || c.field,
          index: typeof c.rowGroupIndex === 'number' ? c.rowGroupIndex : 0
        }))
        .sort((a, b) => a.index - b.index);

      const primary = grouped[0];
      if (!primary) return;

      const primaryId = primary.colId;

      const selectedDef = liveDefs.find((d) => d.field === field || d.colId === field);
      const selectedColId = selectedDef ? selectedDef.colId || selectedDef.field : field;

      const isPrimarySelected = primaryId === selectedColId;

      const state = [{ colId: primaryId, rowGroup: true, rowGroupIndex: 0 }];
      if (!isPrimarySelected) {
        state.push({ colId: selectedColId, rowGroup: true, rowGroupIndex: 1 });
      }

      if (gridApi?.applyColumnState) {
        gridApi.applyColumnState({
          defaultState: { rowGroup: false, rowGroupIndex: null },
          state
        });
      }

      setColDefs((prev) =>
        prev.map((c) => {
          const colId = c.colId || c.field;
          if (colId === primaryId) return { ...c, rowGroup: true, rowGroupIndex: 0 };
          if (!isPrimarySelected && colId === selectedColId)
            return { ...c, rowGroup: true, rowGroupIndex: 1 };
          return { ...c, rowGroup: false, rowGroupIndex: null };
        })
      );
    },
    [gridApi, setColDefs, clearSubGrouping, colDefs]
  );

  const options = useMemo(() => {
    const columnOptions = colDefs.map((col) => ({
      label: col.headerName,
      value: col.field,
      onClick: () => subGroupByField(col.field)
    }));
    return [
      { label: 'No sub-grouping', value: null, onClick: () => clearSubGrouping() },
      ...columnOptions
    ];
  }, [colDefs, subGroupByField, clearSubGrouping]);

  if (!currentPrimaryGroup) return null;

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <Typography
        icon="grid-4-mono"
        iconColor="neutral-700"
        variant="label"
        size="medium"
        className="whitespace-nowrap">
        Sub Group
      </Typography>
      <Popover
        position="left"
        panelClassName="p-0 border border-neutral-100 shadow-spread-medium rounded border-solid gap-y-1"
        optionGroupLabelClassName="h-10 px-4 py-2"
        options={options}
        customButtonTrigger={({ open }) => (
          <Button
            size="xs"
            text={currentSubGroup ? currentSubGroup.headerName : 'No sub-grouping'}
            variant="tertiary"
            iconColor="neutral"
            iconRight={open ? 'chevron-up-linear' : 'chevron-down-linear'}
            iconShade={700}
            className={cn('whitespace-nowrap', open && 'bg-neutral-100')}
          />
        )}
      />
    </div>
  );
};

export default SubGroupOptions;
